'use client'

import { useEffect, useRef, useState } from 'react'
import { Input, Label, TextField } from '@heroui/react'

import { Checkbox } from '@/components/ui/checkbox'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { getVehicleMakes, getVehicleModels, getVehicleYears } from '@/lib/services/actions/inquiry'

export interface VehicleValue {
  year: string
  make: string
  model: string
}

interface VehiclePickerProps {
  value: VehicleValue
  onChange: (value: VehicleValue) => void
  /** Prefill from a URL or a previous step; applied once on mount. */
  initial?: Partial<VehicleValue>
}

const EMPTY: VehicleValue = { year: '', make: '', model: '' }

/**
 * Year → make → model chained selects backed by the VehicleDefinition table,
 * with a "not listed" escape hatch that swaps in free-text make/model so a
 * customer with an unusual vehicle can still submit. Shared by the quote and
 * warranty forms.
 */
export function VehiclePicker({ value, onChange, initial }: VehiclePickerProps) {
  const [years, setYears] = useState<number[]>([])
  const [makes, setMakes] = useState<string[]>([])
  const [models, setModels] = useState<string[]>([])
  const [manual, setManual] = useState(false)
  // Prefill runs once; the cascade effects below must not wipe it.
  const hydrating = useRef(Boolean(initial?.year || initial?.make || initial?.model))

  useEffect(() => {
    getVehicleYears().then(setYears).catch(() => setYears([]))
    if (!hydrating.current) return
    const init = { ...EMPTY, ...initial }
    onChange(init)
    const y = Number(init.year)
    if (y) {
      getVehicleMakes(y)
        .then((ms) => {
          setMakes(ms)
          if (init.make && !ms.includes(init.make)) setManual(true)
          return init.make ? getVehicleModels(y, init.make) : []
        })
        .then((mods) => {
          setModels(mods)
          if (init.model && !mods.includes(init.model)) setManual(true)
        })
        .catch(() => setManual(true))
        .finally(() => {
          hydrating.current = false
        })
    } else {
      if (init.make || init.model) setManual(true)
      hydrating.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setYear = (year: string) => {
    onChange({ year, make: '', model: '' })
    setMakes([])
    setModels([])
    if (year && !manual) getVehicleMakes(Number(year)).then(setMakes).catch(() => setMakes([]))
  }
  const setMake = (make: string) => {
    onChange({ ...value, make, model: '' })
    setModels([])
    if (make && value.year && !manual) {
      getVehicleModels(Number(value.year), make).then(setModels).catch(() => setModels([]))
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select
          label="Year"
          aria-label="Vehicle year"
          placeholder="Year"
          selectedKeys={value.year ? [value.year] : []}
          onSelectionChange={(keys) => setYear(String(Array.from(keys as Iterable<unknown>)[0] ?? ''))}
          variant="outline"
        >
          {years.map((y) => (
            <ListBoxItem key={String(y)} id={String(y)}>
              {String(y)}
            </ListBoxItem>
          ))}
        </Select>

        {manual ? (
          <>
            <TextField value={value.make} onChange={(make) => onChange({ ...value, make })}>
              <Label>Make</Label>
              <Input variant="outline" placeholder="e.g. Tesla" />
            </TextField>
            <TextField value={value.model} onChange={(model) => onChange({ ...value, model })}>
              <Label>Model</Label>
              <Input variant="outline" placeholder="e.g. Cybertruck" />
            </TextField>
          </>
        ) : (
          <>
            <Select
              label="Make"
              aria-label="Vehicle make"
              placeholder="Make"
              selectedKeys={value.make ? [value.make] : []}
              onSelectionChange={(keys) => setMake(String(Array.from(keys as Iterable<unknown>)[0] ?? ''))}
              variant="outline"
              isDisabled={!value.year}
            >
              {makes.map((m) => (
                <ListBoxItem key={m} id={m}>
                  {m}
                </ListBoxItem>
              ))}
            </Select>
            <Select
              label="Model"
              aria-label="Vehicle model"
              placeholder="Model"
              selectedKeys={value.model ? [value.model] : []}
              onSelectionChange={(keys) =>
                onChange({ ...value, model: String(Array.from(keys as Iterable<unknown>)[0] ?? '') })
              }
              variant="outline"
              isDisabled={!value.make}
            >
              {models.map((m) => (
                <ListBoxItem key={m} id={m}>
                  {m}
                </ListBoxItem>
              ))}
            </Select>
          </>
        )}
      </div>
      <Checkbox
        isSelected={manual}
        onChange={(selected) => {
          setManual(selected)
          if (selected) return
          // Back to the lists: clear free-text values the lists cannot represent.
          onChange({ ...value, make: '', model: '' })
          if (value.year) getVehicleMakes(Number(value.year)).then(setMakes).catch(() => setMakes([]))
        }}
      >
        <span className="text-sm">My vehicle isn&apos;t in the list</span>
      </Checkbox>
    </div>
  )
}
